import React, { useCallback, useEffect, useState } from "react";
import InputDropSearch from "../../../../components/inputs/InputDropSearch";
import {
  clinicProductRelease,
  searchReleaseEmployees,
} from "../../../../api/inventoryService";
import ReleaseProductModal from "../components/ReleaseProductModal";
import ReleaseLinesTable from "../components/ReleaseLinesTable";
import { ReleaseLine, staffName } from "../components/releaseTypes";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

/** Products taken for clinic use by a staff member: stock only, no payment. */
function ClinicRelease() {
  const [employeeId, setEmployeeId] = useState("");
  const [lines, setLines] = useState<ReleaseLine[]>([]);
  const [employees, setEmployees] = useState<Employees[]>([]);
  const [open, setopen] = useState(false);

  const getEmployeesData = useCallback(async (search: string) => {
    try {
      const data = await searchReleaseEmployees(search);
      setEmployees(
        (data as Employees[]).map((e) => ({ ...e, fullName: staffName(e) }))
      );
    } catch (error) {
      console.error(error);
    }
  }, []);
  useEffect(() => {
    getEmployeesData("");
  }, [getEmployeesData]);

  const addProduct = useCallback((product: ReleaseLine) => {
    setLines((prev) => [...prev, product]);
    setopen(false);
  }, []);
  const removeProductById = useCallback((index: number) => {
    setLines((prev) => prev.filter((_line, i) => i !== index));
  }, []);
  const navigate = useNavigate();

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (!employeeId) {
      toast.error("Choose who is taking the products.");
      return;
    }
    if (lines.length === 0) {
      toast.error("Add a product first.");
      return;
    }

    clinicProductRelease({
      employeeId,
      product: lines.map((line) => ({
        productId: line.productId,
        productName: line.productName,
        quantity: line.quantity,
      })),
    })
      .then(() => {
        navigate("/client/inventory/release/all-release?type=clinic");
      })
      .catch((error) => {
        console.error(error?.response);
        toast.error(
          error?.response?.data?.message || "Failed to save the release."
        );
      });
  };
  return (
    <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
      {open && (
        <ReleaseProductModal
          title="Add Product for Clinic Use"
          releaseType="clinic"
          priced={false}
          addProduct={addProduct}
          closeModal={() => setopen(false)}
        />
      )}

      <form onSubmit={handleSubmit} className="p-3 space-y-3">
        <InputDropSearch
          data={employees}
          name="employeeId"
          label="Taken by (Staff)"
          selectName1="fullName"
          selectValue="_id"
          onChange={(e) => setEmployeeId(e.target.value)}
          onSearch={getEmployeesData}
        />

        <button
          type="button"
          onClick={() => setopen(true)}
          className="w-full px-3 py-2 rounded-lg border-2 border-dashed border-primary/30 dark:border-primary/50 text-primary font-medium hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors flex items-center justify-center gap-1.5 text-xs"
        >
          <span className="text-base">+</span> Add Product
        </button>

        <ReleaseLinesTable lines={lines} priced={false} onRemove={removeProductById} />

        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Stock is taken out of inventory. No payment is recorded.
        </p>

        <button
          type="submit"
          className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-semibold hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
        >
          Add Release
        </button>
      </form>
    </div>
  );
}

export default ClinicRelease;
interface Employees {
  _id: string;
  firstName: string;
  lastName?: string;
  fullName?: string;
}
